import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  RecommendedModelProfileView,
  RecommendedStepId,
} from '../../../../shared-types/src/recommended-models.js';
import { normalizeRecommendedDescription, RECOMMENDED_DESCRIPTION_MAX_LENGTH } from '../../../../shared-types/src/recommended-models.js';
import type { RecommendedThinkingLevel } from '../../../../shared-types/src/model-thinking.js';
import { modelCustomerLabel } from '../model-customer-label.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import { describeError } from '../../errors.js';
import {
  canSaveRecommendedDraft,
  changeRecommendedModel,
  recommendedDraft,
  recommendedDraftErrors,
  saveRecommendedDraft,
} from './recommended-models-form.js';

export function RecommendedModelsPanel({
  http,
  tenantId,
  onToast,
  active = true,
  onOpenSettings,
}: {
  http: AdminHttpClient;
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
  active?: boolean;
  onOpenSettings?: (destination: 'manualProviderModels' | 'assignment' | 'modelDeployment' | 'loginDefault' | 'modelAccess') => void;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<RecommendedModelProfileView>();
  const [draft, setDraft] = useState(recommendedDraft);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(2);
  const [conflict, setConflict] = useState<RecommendedModelProfileView>();
  const [message, setMessage] = useState('');
  const [replacement, setReplacement] = useState<{ id: RecommendedStepId; description: string }>();
  const sequence = useRef(0);
  const saving = useRef(false);
  const translate = useRef(t);
  translate.current = t;
  const latestView = useRef(view);
  latestView.current = view;
  const wasActive = useRef(active);
  useEffect(() => {
    const returning = active && !wasActive.current;
    wasActive.current = active;
    const previous = latestView.current;
    if (!returning || !previous || previous.tenantId !== tenantId) return;
    const request = ++sequence.current;
    setBusy(true);
    void http.getRecommendedModelProfile(tenantId).then(next => {
      if (sequence.current !== request || next.tenantId !== tenantId) return;
      if ((next.profile?.revision ?? 0) !== (previous.profile?.revision ?? 0)) setConflict(next);
      else setView(next);
    }).catch(error => {
      if (sequence.current === request) onToast({ tone: 'error', message: describeError(error, translate.current) });
    }).finally(() => {
      if (sequence.current === request) setBusy(false);
    });
  }, [active, http, tenantId, onToast]);
  useEffect(() => {
    const request = ++sequence.current;
    setView(undefined);
    setDraft(recommendedDraft());
    setConflict(undefined);
    setMessage('');
    setReplacement(undefined);
    if (!tenantId) return;
    setBusy(true);
    void http
      .getRecommendedModelProfile(tenantId)
      .then((next) => {
        if (sequence.current !== request || next.tenantId !== tenantId) return;
        setView(next);
        setDraft(recommendedDraft(next));
      })
      .catch((error) => {
        if (sequence.current === request)
          onToast({ tone: 'error', message: describeError(error, translate.current) });
      })
      .finally(() => {
        if (sequence.current === request) setBusy(false);
      });
    return () => {
      sequence.current = request + 1;
    };
  }, [http, tenantId, onToast]);

  const save = async () => {
    if (!view || busy || saving.current || conflict || !canSaveRecommendedDraft(draft, view))
      return;
    saving.current = true;
    setBusy(true);
    setMessage('');
    const request = sequence.current;
    try {
      const result = await saveRecommendedDraft(
        draft,
        () => http.putRecommendedModelProfile(tenantId, draft, view.profile?.revision ?? 0),
        () => http.getRecommendedModelProfile(tenantId)
      );
      if (sequence.current !== request || result.view.tenantId !== tenantId) return;
      if (result.kind === 'conflict') setConflict(result.view);
      else {
        setView(result.view);
        setDraft(recommendedDraft(result.view));
        setMessage(t('recommendedModels.saved'));
      }
    } catch (error) {
      if (sequence.current === request)
        onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      saving.current = false;
      if (sequence.current === request) setBusy(false);
    }
  };
  const errors = view ? recommendedDraftErrors(draft, view) : [];
  const updateDescription = (id: RecommendedStepId, description: string) => {
    setDraft(current => ({ ...current, steps: current.steps.map(step =>
      step.id === id ? { ...step, description } : step) }));
    setReplacement(undefined);
  };
  const proposeDescription = (id: RecommendedStepId, description: string) => {
    const current = draft.steps.find(step => step.id === id)?.description;
    if (current && current !== description) setReplacement({ id, description });
    else updateDescription(id, description);
  };
  const examples = ['simple', 'drafting', 'everyday', 'comparison', 'analysis'] as const;
  return (
    <section className="panel content-stack recommended-model-panel" aria-busy={busy}>
      <h2>{t('recommendedModels.title')}</h2>
      <p>{t('recommendedModels.help')}</p>
      <p>{t('recommendedModels.thinkingHelp')}</p>
      {onOpenSettings && <nav aria-label={t('recommendedModels.relatedSettings')}>
        {(['manualProviderModels', 'assignment', 'modelDeployment', 'loginDefault', 'modelAccess'] as const).map(destination => (
          <button key={destination} type="button" onClick={() => onOpenSettings(destination)}>{t(`recommendedModels.links.${destination}`)}</button>
        ))}
      </nav>}
      {!view ? (
        <p role="status">
          {t(busy ? 'recommendedModels.loading' : 'recommendedModels.unavailable')}
        </p>
      ) : (
        <>
          <details>
            <summary>{t('recommendedModels.candidateDetails', { count: view.candidates.length })}</summary>
            {!view.candidates.length && <p>{t('recommendedModels.noCandidates')}</p>}
            {view.candidates.map(candidate => <article key={candidate.model.id}>
              <h3>{modelCustomerLabel(candidate.model)}</h3>
              <p>{candidate.model.displayName} · {candidate.model.id}</p>
              <p>{t(candidate.defaultMember ? 'recommendedModels.allMembers' : 'recommendedModels.assignmentRequired')}{candidate.loginDefault ? ` · ${t('recommendedModels.loginDefault')}` : ''}</p>
              {candidate.billingStatus && <p>{t(`recommendedModels.billing.${candidate.billingStatus}`)}</p>}
              {candidate.thinkingStatus === 'unconfigured' && <p>{t('recommendedModels.unconfigured')}</p>}
              {candidate.reasons.map(reason => <p key={reason}>{t(`recommendedModels.reasons.${reason}`)}</p>)}
              {candidate.levelSupport?.filter(support => support.reasons.length > 0).map(support => <p key={support.level}>
                {t(`recommendedModels.levels.${support.level}`)}: {support.reasons.map(reason => t(`recommendedModels.reasons.${reason}`)).join(' · ')}
              </p>)}
            </article>)}
          </details>
          <fieldset disabled={busy} className="content-stack recommended-model-fields">
            <label>
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              />{' '}
              {t('recommendedModels.enabled')}
            </label>
            <p className="recommended-model-help">{t('recommendedModels.activationHelp')}</p>
            {draft.steps.map((step, index) => (
              <div key={step.id} className="recommended-model-row">
                <h3>
                  {t('recommendedModels.step', { step: step.id })}{' '}
                  {step.id === '3' && <span>{t('recommendedModels.default')}</span>}
                </h3>
                <label>
                  {t('recommendedModels.model')}
                  <select
                    value={step.catalogModelId}
                    onChange={(e) => {
                      setDraft(changeRecommendedModel(draft, step.id, e.target.value, view));
                      setReplacement(undefined);
                    }}
                  >
                    <option value="">{t('recommendedModels.choose')}</option>
                    {step.catalogModelId &&
                      !view.candidates.some((c) => c.model.id === step.catalogModelId) && (
                        <option value={step.catalogModelId}>
                          {t('recommendedModels.unavailable')}
                        </option>
                      )}
                    {view.candidates.map((c) => (
                      <option key={c.model.id} value={c.model.id}>
                        {modelCustomerLabel(c.model)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('recommendedModels.effort')}
                  <select
                    value={step.thinkingLevel}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        steps: draft.steps.map((s) =>
                          s.id === step.id
                            ? { ...s, thinkingLevel: e.target.value as RecommendedThinkingLevel | '' }
                            : s
                        ),
                      })
                    }
                  >
                    <option value="">{t('recommendedModels.choose')}</option>
                    {step.thinkingLevel && !view.candidates.find(c => c.model.id === step.catalogModelId)?.thinkingLevels.includes(step.thinkingLevel) && <option value={step.thinkingLevel}>{t(`recommendedModels.levels.${step.thinkingLevel}`)} · {t('recommendedModels.unavailable')}</option>}
                    {view.candidates
                      .find((c) => c.model.id === step.catalogModelId)
                      ?.thinkingLevels.map((level) => (
                        <option key={level} value={level}>
                          {t(`recommendedModels.levels.${level}`)}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="recommended-model-description">
                  {t('recommendedModels.description')}
                  <textarea
                    value={step.description}
                    rows={2}
                    aria-invalid={errors[index]?.includes('descriptionTooLong') || undefined}
                    aria-describedby={`recommended-description-count-${step.id}${errors[index]?.includes('descriptionTooLong') ? ` recommended-description-error-${step.id}` : ''}`}
                    onChange={event => updateDescription(step.id, event.target.value)}
                  />
                  <span id={`recommended-description-count-${step.id}`}>
                    {t('recommendedModels.descriptionCount', { count: normalizeRecommendedDescription(step.description).length, max: RECOMMENDED_DESCRIPTION_MAX_LENGTH })}
                  </span>
                </label>
                <div className="recommended-model-description-actions">
                  <label>{t('recommendedModels.example')}
                    <select value="" onChange={event => {
                      if (event.target.value) proposeDescription(step.id, t(`recommendedModels.examples.${event.target.value}`));
                    }}>
                      <option value="">{t('recommendedModels.choose')}</option>
                      {examples.map(key => <option key={key} value={key}>{t(`recommendedModels.exampleLabels.${key}`)}</option>)}
                    </select>
                  </label>
                  {view.candidates.find(c => c.model.id === step.catalogModelId)?.model.description &&
                    <button type="button" onClick={() => proposeDescription(step.id,
                      view.candidates.find(c => c.model.id === step.catalogModelId)!.model.description!)}>
                      {t('recommendedModels.importModelDescription')}
                    </button>}
                  {view.profile?.steps[index]?.thinkingLevel !== step.thinkingLevel &&
                    view.profile?.steps[index]?.catalogModelId === step.catalogModelId && step.description &&
                    <p>{t('recommendedModels.reviewEffortDescription')}</p>}
                </div>
                {replacement?.id === step.id && <div className="recommended-model-replacement" role="status">
                  <p>{t('recommendedModels.confirmReplace')}</p>
                  <button type="button" onClick={() => updateDescription(step.id, replacement.description)}>{t('recommendedModels.replace')}</button>
                  <button type="button" onClick={() => setReplacement(undefined)}>{t('recommendedModels.cancelReplace')}</button>
                </div>}
                {errors[index]?.includes('descriptionTooLong') &&
                  <p id={`recommended-description-error-${step.id}`} role="alert" className="recommended-model-row-notice">
                    {t('recommendedModels.descriptionTooLong')}
                  </p>}
                {!!errors[index]?.filter(error => error !== 'descriptionTooLong').length && (
                  <p role="status" className="recommended-model-row-notice">
                    {errors[index]?.filter(error => error !== 'descriptionTooLong').map((error) => t(`recommendedModels.${error}`)).join(' · ')}
                  </p>
                )}
                {view.candidates.find(c => c.model.id === step.catalogModelId)?.reasons.map((reason) => (
                  <p key={reason}>{t(`recommendedModels.reasons.${reason}`)}</p>
                ))}
              </div>
            ))}
          </fieldset>
          <h3>{t('recommendedModels.preview')}</h3>
          <div className="recommended-model-preview">
            {draft.steps.map((step, index) => {
              const model = view.candidates.find(c => c.model.id === step.catalogModelId)?.model;
              return <button key={step.id} type="button" aria-pressed={preview === index}
                onClick={() => setPreview(index)}>
                <strong>{t('recommendedModels.step', { step: step.id })}{step.id === '3' ? ` · ${t('recommendedModels.default')}` : ''}</strong>
                <span>{model ? modelCustomerLabel(model) : t('recommendedModels.unavailable')} · {step.thinkingLevel
                  ? t(`recommendedModels.levels.${step.thinkingLevel}`) : t('recommendedModels.choose')}</span>
                {step.description && <span className="recommended-model-preview-description">{step.description}</span>}
              </button>;
            })}
          </div>
          <p>{t('recommendedModels.previewHelp')}</p>
          {conflict && (
            <div role="alert">
              <p>{t('recommendedModels.conflict')}</p>
              <p>{t(conflict.profile?.enabled ? 'recommendedModels.serverEnabled' : 'recommendedModels.serverDisabled', { revision: conflict.profile?.revision ?? 0 })}</p>
              {conflict.steps.map((step) => (
                <p key={step.id}>
                  {step.id}:{' '}
                  {conflict.candidates.find((c) => c.model.id === step.catalogModelId)?.model
                    .displayName ?? t('recommendedModels.unavailable')}{' '}
                  · {t(`recommendedModels.levels.${step.thinkingLevel}`)}
                  {' · '}{t('recommendedModels.serverDescription')}: {step.description || t('recommendedModels.emptyDescription')}
                  {' / '}{t('recommendedModels.draftDescription')}: {draft.steps.find(item => item.id === step.id)?.description || t('recommendedModels.emptyDescription')}
                </p>
              ))}
              <button
                type="button"
                onClick={() => {
                  setView(conflict);
                  setConflict(undefined);
                }}
              >
                {t('recommendedModels.keepDraft')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setView(conflict);
                  setDraft(recommendedDraft(conflict));
                  setConflict(undefined);
                }}
              >
                {t('recommendedModels.reload')}
              </button>
            </div>
          )}
          <button
            type="button"
            disabled={busy || !!conflict || !canSaveRecommendedDraft(draft, view)}
            onClick={() => void save()}
          >
            {t('recommendedModels.save')}
          </button>
          <p role="status">{message}</p>
          {errors.some(row => row.length) && <p role="status">{[...new Set(errors.flat())].map(error => t(`recommendedModels.${error}`)).join(' · ')}</p>}
        </>
      )}
    </section>
  );
}
